import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  IncidentActionSnapshotRollupResponse,
  IncidentEscalationRollupResponse,
  IncidentGroup,
  IncidentGroupHeatmapResponse,
  IncidentCorrectiveActionRollupResponse,
  IncidentReviewOutcomeHistoryResponse,
  IncidentKpis,
  IncidentMeasurementRollupResponse,
  IncidentOpsQueuesResponse,
  IncidentOwnershipRollupResponse,
  IncidentReviewOutcomeRollupResponse,
} from '../../services/incidentsApi';
import { incidentsApi } from '../../services/incidentsApi';
import { ensureUiScope } from '../../services/api';
import { camerasApi } from '../../services/camerasApi';
import { metricsApi, type SafetySummary } from '../../services/metricsApi';
import { zonesApi, type Zone } from '../../services/zonesApi';
import { policiesApi, type Policy } from '../../services/policiesApi';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import {
  deriveDateRangeFromAnalyticsRange,
  deriveHoursFromAnalyticsRange,
  type AnalyticsTimeRange,
  type BreakdownPoint,
  type IncidentFlowPoint,
} from './incidentAnalyticsModel';

type SeriesPoint = Record<string, string | number>;

type ZoneStatusPoint = {
  zone_id: string;
  name: string;
  occupancy: number | null;
};

type SoftLoadResult<T> = {
  ok: boolean;
  value: T;
};

const EMPTY_SUMMARY: SafetySummary = {
  ppe_violations: 0,
  zone_intrusions: 0,
  active_alerts: 0,
  cameras_online: 0,
  avg_latency_ms: 0,
};

function normalizeMetricKey(value: string): string {
  return String(value || '').trim().replace(/[\s-]+/g, '_').toLowerCase();
}

function transformPrometheusData(result: any[]): SeriesPoint[] {
  if (!Array.isArray(result) || !result.length) return [];

  const timestamps = new Set<number>();
  result.forEach((series) => {
    (series?.values || []).forEach((value: [number, string]) => timestamps.add(Number(value[0] || 0)));
  });

  return Array.from(timestamps)
    .sort((a, b) => a - b)
    .map((timestamp) => {
      const point: SeriesPoint = {
        time: new Date(timestamp * 1000).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        timestamp,
      };
      result.forEach((series) => {
        const zone = String(series?.metric?.zone || 'total').trim();
        const value = (series?.values || []).find((item: [number, string]) => Number(item[0] || 0) === timestamp);
        point[zone] = value ? parseFloat(String(value[1] || 0)) : 0;
      });
      return point;
    });
}

function buildZoneStatusFallbackSeries(zoneStatusItems: ZoneStatusPoint[]): SeriesPoint[] {
  if (!zoneStatusItems.length) return [];
  return [
    zoneStatusItems.reduce((point: SeriesPoint, zone) => {
      point.time = 'Now';
      point.timestamp = Date.now() / 1000;
      point[String(zone.zone_id || zone.name || '').trim()] = Number(zone.occupancy || 0);
      return point;
    }, {}),
  ];
}

async function loadWithFallback<T>(promise: Promise<T>, fallback: T, timeoutMs: number): Promise<SoftLoadResult<T>> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    const value = await Promise.race<T>([
      promise,
      new Promise<T>((resolve) => {
        timer = setTimeout(() => resolve(fallback), timeoutMs);
      }),
    ]);
    return {
      ok: value !== fallback,
      value,
    };
  } catch {
    return {
      ok: false,
      value: fallback,
    };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function waitMs(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export function useIncidentAnalyticsOverview(args: {
  timeRange: AnalyticsTimeRange;
  selectedZone: string;
  heatmapDimension: 'zone' | 'camera';
  heatmapIncidentType: string;
}) {
  const { timeRange, selectedZone, heatmapDimension, heatmapIncidentType } = args;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<IncidentKpis | null>(null);
  const [opsQueues, setOpsQueues] = useState<IncidentOpsQueuesResponse | null>(null);
  const [groups, setGroups] = useState<IncidentGroup[]>([]);
  const [incidentFlowData, setIncidentFlowData] = useState<IncidentFlowPoint[]>([]);
  const [severityData, setSeverityData] = useState<BreakdownPoint[]>([]);
  const [categoryData, setCategoryData] = useState<BreakdownPoint[]>([]);
  const [zoneOccupancyData, setZoneOccupancyData] = useState<SeriesPoint[]>([]);
  const [safetySummary, setSafetySummary] = useState<SafetySummary>(EMPTY_SUMMARY);
  const [zoneOptions, setZoneOptions] = useState<string[]>([]);
  const [zoneLabels, setZoneLabels] = useState<Record<string, string>>({});
  const [zoneStatuses, setZoneStatuses] = useState<ZoneStatusPoint[]>([]);
  const [heatmap, setHeatmap] = useState<IncidentGroupHeatmapResponse | null>(null);
  const [cameraNameById, setCameraNameById] = useState<Record<string, string>>({});
  const [sitePolicies, setSitePolicies] = useState<Policy[]>([]);
  const [siteZones, setSiteZones] = useState<Zone[]>([]);
  const [reviewOutcomeRollup, setReviewOutcomeRollup] = useState<IncidentReviewOutcomeRollupResponse | null>(null);
  const [reviewOutcomeHistory, setReviewOutcomeHistory] = useState<IncidentReviewOutcomeHistoryResponse | null>(null);
  const [ownershipRollup, setOwnershipRollup] = useState<IncidentOwnershipRollupResponse | null>(null);
  const [correctiveActionRollup, setCorrectiveActionRollup] = useState<IncidentCorrectiveActionRollupResponse | null>(null);
  const [measurementRollup, setMeasurementRollup] = useState<IncidentMeasurementRollupResponse | null>(null);
  const [actionSnapshotRollup, setActionSnapshotRollup] = useState<IncidentActionSnapshotRollupResponse | null>(null);
  const [escalationRollup, setEscalationRollup] = useState<IncidentEscalationRollupResponse | null>(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrentRequest = () => requestIdRef.current === requestId;

    setLoading(true);
    setError(null);
    setCameraNameById({});
    setSitePolicies([]);
    setReviewOutcomeRollup(null);
    setReviewOutcomeHistory(null);
    setOwnershipRollup(null);
    setCorrectiveActionRollup(null);
    setMeasurementRollup(null);
    setActionSnapshotRollup(null);
    setEscalationRollup(null);
    setZoneOccupancyData([]);

    try {
      const hours = deriveHoursFromAnalyticsRange(timeRange);
      const { fromDate, toDate } = deriveDateRangeFromAnalyticsRange(timeRange);
      const zoneFilter = selectedZone !== 'all' ? selectedZone : undefined;

      const scope = ensureUiScope();
      const siteIdForPolicies = String(scope?.siteId || '').trim() || 'default';
      const orgIdForPolicies = String(scope?.orgId || '').trim() || undefined;

      const [
        zonesResult,
        zoneStatusResult,
        kpisResult,
        opsQueuesResult,
        groupsResult,
        analyticsResult,
        safetySummaryResult,
        heatmapResult,
      ] = await Promise.all([
        loadWithFallback(zonesApi.listZones(), { zones: [], count: 0 }, 10000),
        loadWithFallback(zonesApi.getAllZonesStatus(), { zones: [], count: 0, timestamp: '' }, 10000),
        loadWithFallback(incidentsApi.getKpis({ from_date: fromDate, to_date: toDate }), null, 25000),
        loadWithFallback(incidentsApi.getOpsQueues({ limit: 25 }), null, 20000),
        loadWithFallback(
          incidentsApi.listIncidentGroups({
            status: 'all',
            from_date: fromDate,
            to_date: toDate,
            zone_id: zoneFilter,
          }),
          { items: [] as IncidentGroup[] },
          25000,
        ),
        loadWithFallback(
          metricsApi.getAnalyticsData(hours),
          {
            incident_flow_over_time: [] as IncidentFlowPoint[],
            ppe_over_time: [] as any[],
            zone_over_time: [] as any[],
            alerts_by_category: [] as BreakdownPoint[],
            severity_breakdown: [] as BreakdownPoint[],
            totals: { ppe_violations: 0, zone_breaches: 0, total_alerts: 0 },
          },
          20000,
        ),
        loadWithFallback(metricsApi.getSafetySummary(), EMPTY_SUMMARY, 10000),
        loadWithFallback(
          incidentsApi.getIncidentGroupHeatmap({
            dimension: heatmapDimension,
            from_date: fromDate,
            to_date: toDate,
            incident_type: heatmapIncidentType || undefined,
            zone_id: zoneFilter,
            limit_rows: 8,
          }),
          null,
          20000,
        ),
      ]);

      if (!isCurrentRequest()) return;

      const zonesRes = zonesResult.value;
      const zoneStatusRes = zoneStatusResult.value;
      const kpisRes = kpisResult.value;
      const opsQueuesRes = opsQueuesResult.value;
      const groupsRes = groupsResult.value;
      const analyticsData = analyticsResult.value;
      const safetySummaryRes = safetySummaryResult.value;
      const heatmapRes = heatmapResult.value;

      const nextZoneLabels: Record<string, string> = {};
      const zoneStatusItems: ZoneStatusPoint[] = Array.isArray(zoneStatusRes?.zones) ? zoneStatusRes.zones : [];
      const addZoneAlias = (raw: string | undefined, label: string | undefined) => {
        if (!raw || !label) return;
        nextZoneLabels[raw] = label;
        nextZoneLabels[normalizeMetricKey(raw)] = label;
      };

      (zonesRes?.zones || []).forEach((zone: any) => {
        const label = presentAreaLabel(String(zone?.name || zone?.zone_id || zone?.id || '').trim());
        addZoneAlias(String(zone?.id || '').trim(), label);
        addZoneAlias(String(zone?.zone_id || '').trim(), label);
        addZoneAlias(String(zone?.name || '').trim(), label);
      });
      zoneStatusItems.forEach((zone) => {
        addZoneAlias(String(zone?.zone_id || '').trim(), presentAreaLabel(String(zone?.name || '').trim()));
        addZoneAlias(String(zone?.name || '').trim(), presentAreaLabel(String(zone?.name || '').trim()));
      });

      setZoneLabels(nextZoneLabels);
      setZoneStatuses(zoneStatusItems);
      setZoneOptions(
        Array.from(
          new Set(
            (zonesRes?.zones || [])
              .map((zone: any) => String(zone?.zone_id || zone?.id || zone?.name || '').trim())
              .filter(Boolean),
          ),
        ).sort(),
      );

      setKpis(kpisRes);
      setOpsQueues(opsQueuesRes);
      setGroups(Array.isArray(groupsRes?.items) ? groupsRes.items : []);
      setIncidentFlowData(Array.isArray(analyticsData?.incident_flow_over_time) ? analyticsData.incident_flow_over_time : []);
      setSeverityData(Array.isArray(analyticsData?.severity_breakdown) ? analyticsData.severity_breakdown : []);
      setCategoryData(Array.isArray(analyticsData?.alerts_by_category) ? analyticsData.alerts_by_category : []);
      setSafetySummary(safetySummaryRes || EMPTY_SUMMARY);
      setHeatmap(heatmapRes);
      setSiteZones(Array.isArray(zonesRes?.zones) ? (zonesRes.zones as Zone[]) : []);
      setZoneOccupancyData(buildZoneStatusFallbackSeries(zoneStatusItems));

      const coreFailures = [
        zonesResult,
        zoneStatusResult,
        kpisResult,
        opsQueuesResult,
        groupsResult,
        analyticsResult,
        safetySummaryResult,
        heatmapResult,
      ].filter((result) => !result.ok).length;
      const hasCoreData =
        Boolean(kpisRes)
        || Boolean(opsQueuesRes)
        || Boolean(heatmapRes)
        || Boolean((groupsRes?.items || []).length)
        || Boolean((analyticsData?.incident_flow_over_time || []).length)
        || Boolean((zonesRes?.zones || []).length)
        || Boolean((zoneStatusRes?.zones || []).length);
      if (coreFailures > 0 && hasCoreData) {
        setError('Some analytics panels are delayed. Showing available data.');
      } else if (coreFailures > 0) {
        setError('Failed to load incident analytics.');
      }
      setLoading(false);

      void (async () => {
        const siteId = String(scope?.siteId || '').trim();
        const occupancyStep = hours <= 1 ? '60s' : hours <= 6 ? '5m' : hours <= 24 ? '15m' : '1h';
        await waitMs(600);
        if (!isCurrentRequest()) return;

        const policiesResult = await loadWithFallback(
          policiesApi.listPolicies(siteIdForPolicies, true, undefined, orgIdForPolicies),
          { policies: [], count: 0 },
          15000,
        );
        if (!isCurrentRequest()) return;
        setSitePolicies(Array.isArray(policiesResult.value?.policies) ? policiesResult.value.policies : []);

        if (siteId) {
          const camerasResult = await loadWithFallback(
            camerasApi.listCameraBootstrap(siteId),
            { ok: true, items: [] as any[], count: 0 },
            12000,
          );
          if (!isCurrentRequest()) return;
          const nextCameraNames: Record<string, string> = {};
          (camerasResult.value?.items || []).forEach((cam: { camera_id?: string; name?: string; label?: string }) => {
            const id = String(cam?.camera_id || '').trim();
            if (!id) return;
            const label = String(cam?.name || cam?.label || '').trim() || id;
            nextCameraNames[id] = label;
          });
          setCameraNameById(nextCameraNames);
        }

        const occupancyResult = await loadWithFallback(
          metricsApi.queryMetrics('avg(warehouse_aisle_occupancy) by (zone)', fromDate, toDate, occupancyStep),
          null,
          12000,
        );
        if (!isCurrentRequest()) return;
        const transformedOccupancy = transformPrometheusData(occupancyResult.value?.data?.result || []);
        if (transformedOccupancy.length) {
          setZoneOccupancyData(transformedOccupancy);
        }

        const rollupTasks: Array<() => Promise<void>> = [
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getReviewOutcomeRollup({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setReviewOutcomeRollup(result.value);
          },
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getReviewOutcomeHistory({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setReviewOutcomeHistory(result.value);
          },
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getOwnershipRollup({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setOwnershipRollup(result.value);
          },
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getCorrectiveActionRollup({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setCorrectiveActionRollup(result.value);
          },
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getMeasurementRollup({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setMeasurementRollup(result.value);
          },
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getActionSnapshotRollup({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setActionSnapshotRollup(result.value);
          },
          async () => {
            const result = await loadWithFallback(
              incidentsApi.getEscalationRollup({
                org_id: scope?.orgId || undefined,
                site_id: scope?.siteId || undefined,
                from_ts: fromDate,
                to_ts: toDate,
                zone_id: zoneFilter,
              }),
              null,
              20000,
            );
            if (isCurrentRequest()) setEscalationRollup(result.value);
          },
        ];

        for (const task of rollupTasks) {
          if (!isCurrentRequest()) return;
          await task();
          if (!isCurrentRequest()) return;
          await waitMs(150);
        }
      })();
    } catch (err: any) {
      if (!isCurrentRequest()) return;
      setError(err?.message || 'Failed to load incident analytics.');
      setKpis(null);
      setOpsQueues(null);
      setGroups([]);
      setIncidentFlowData([]);
      setSeverityData([]);
      setCategoryData([]);
      setZoneOccupancyData([]);
      setSafetySummary(EMPTY_SUMMARY);
      setHeatmap(null);
      setCameraNameById({});
      setSitePolicies([]);
      setSiteZones([]);
      setReviewOutcomeRollup(null);
      setReviewOutcomeHistory(null);
      setOwnershipRollup(null);
      setCorrectiveActionRollup(null);
      setMeasurementRollup(null);
      setActionSnapshotRollup(null);
      setEscalationRollup(null);
      setLoading(false);
    }
  }, [heatmapDimension, heatmapIncidentType, selectedZone, timeRange]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    kpis,
    opsQueues,
    groups,
    incidentFlowData,
    severityData,
    categoryData,
    zoneOccupancyData,
    heatmap,
    safetySummary,
    zoneOptions,
    zoneLabels,
    zoneStatuses,
    cameraNameById,
    sitePolicies,
    siteZones,
    reviewOutcomeRollup,
    reviewOutcomeHistory,
    ownershipRollup,
    correctiveActionRollup,
    measurementRollup,
    actionSnapshotRollup,
    escalationRollup,
    refetch: fetchData,
  };
}
